import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useUnreadCount, useNewMessageNotification } from "@/hooks/use-messages";
import { useMerchantUnreadCount, useMarkAllNotificationsRead } from "@/hooks/use-merchant-notifications";
import { useVirtualShopUnreadCount, useMarkAllVirtualShopNotificationsRead } from "@/hooks/use-virtual-shop-notifications";
import { useBuyerUnreadCount, useMarkAllBuyerNotificationsRead } from "@/hooks/use-buyer-notifications";
import { useSocket } from "@/contexts/SocketContext";
import { useFavorites } from "@/hooks/use-favorites";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/contexts/LanguageContext";
import { LANGUAGES } from "@/lib/translations";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Link, useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import { useMediaQuery } from "@/hooks/use-media-query";
import { nativeTabNavigate } from "@/lib/nativeNav";
import { appBack } from "@/lib/appHistory";
import { 
  LogIn, 
  LogOut, 
  Heart, 
  MessageCircle, 
  Package, 
  User,
  Users,
  Plus,
  Settings,
  Shield,
  Sparkles,
  Briefcase,
  Home,
  ArrowRight,
  Store,
  ClipboardList,
  Headphones,
  Bell,
  Clock,
  Map,
  GitCompare,
  Mail,
  Building2,
  Wallet,
  Rocket,
  Globe,
  Scale,
  ShoppingCart,
  Inbox,
  Undo2,
  Menu,
  UserPlus
} from "lucide-react";
import { CartSheet } from "./CartSheet";
import { QuickGuideModal } from "./QuickGuideModal";
import { IraqFlag, TurkeyFlag, ChinaFlag } from "./FlagIcons";
import { hasPhysicalStorefront, isStoreOwnerRole, isWholesaleMerchantOnly, isBuyerOnlyRole, getTodayProductsHref } from "@/lib/userRoles";

export function Header() {
  const { user, logout, isInitialized } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const isRTL = language === "ar";
  const { data: unread } = useUnreadCount(user?.id);
  const isStoreRole = isStoreOwnerRole(user);
  const isWholesaleOnlyRole = isWholesaleMerchantOnly(user);
  // Merchant notifications are inserted server-side for ANY seller that is
  // not a virtual store (wholesalers, store owners, and even regular
  // customers who happen to have posted a listing). The bell visibility
  // (further down) keeps the chrome quiet for users who never receive any.
  const { data: merchantUnread } = useMerchantUnreadCount({
    enabled: !!user?.id,
  });
  const { data: virtualShopUnread } = useVirtualShopUnreadCount({ enabled: !!user?.isVirtualStore });
  const isBuyerRole = isBuyerOnlyRole(user);
  const { data: buyerUnread } = useBuyerUnreadCount({ enabled: !!user?.id });
  const { data: disputeUnread } = useQuery<{ count: number }>({
    queryKey: ["/api/disputes/notifications/unread-count"],
    queryFn: async () => {
      const res = await fetch("/api/disputes/notifications/unread-count", { credentials: "include" });
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    enabled: !!user,
    refetchInterval: 30_000,
  });
  const markAllShopRead = useMarkAllVirtualShopNotificationsRead();
  const markAllBuyerRead = useMarkAllBuyerNotificationsRead();
  const markAllMerchantRead = useMarkAllNotificationsRead();
  const buyerOrderUnreadCount = buyerUnread?.count || 0;
  const disputeUnreadCount = disputeUnread?.count ?? 0;
  const shopOrderUnreadCount = virtualShopUnread?.count || 0;
  // «طلبات إعلاناتي الخاصة» counts ONLY orders on the user's own personal
  // listings (the merchant stream). It must stay independent of the
  // virtual-shop stream — otherwise a dropshipping order on the store
  // catalog wrongly lights up this item too (they shared merchantHubUnreadCount
  // for virtual-store owners, which equals the shop count).
  const myListingOrdersUnreadCount = merchantUnread?.count || 0;

  const accountTypeBadgeClass = isWholesaleOnlyRole
    ? "bg-amber-400/90 text-amber-900"
    : isStoreRole
      ? (hasPhysicalStorefront(user) ? "bg-emerald-400/90 text-emerald-900" : "bg-violet-400/90 text-violet-900")
      : "bg-white/25 text-white";

  const accountTypeBadgeText = isWholesaleOnlyRole
    ? t("header.wholesaler")
    : isStoreRole
      ? (hasPhysicalStorefront(user) ? t("header.shopOwner") : t("header.virtualStore"))
      : t("header.customer");
  
  const messagesUnreadCount = unread?.count || 0;
  // Virtual stores have their own notification stream; everyone else
  // (wholesalers, store owners, regular customers with listings) reads the
  // merchant stream. Buyers with no listings simply get a count of 0.
  const merchantHubUnreadCount = user?.isVirtualStore
    ? (virtualShopUnread?.count || 0)
    : (merchantUnread?.count || 0);

  // Single helper for "the user is opening their merchant/store hub —
  // clear the red dot". Picks the correct stream based on role so a
  // virtual-store owner's shop notifications don't get marked via the
  // normal-merchant endpoint and vice versa. No-op when nothing is unread.
  const clearMerchantHubUnread = () => {
    if (merchantHubUnreadCount <= 0) return;
    if (user?.isVirtualStore) {
      markAllShopRead.mutate();
    } else {
      markAllMerchantRead.mutate();
    }
  };

  useNewMessageNotification(user?.id);
  const { notificationCount, clearNotifications, lastNotifConvId, clearLastNotifConvId } = useSocket();
  const messagesHref = lastNotifConvId ? `/messages?conv=${lastNotifConvId}` : "/messages";
  const handleMessagesClick = () => { clearNotifications(); clearLastNotifConvId(); };
  const merchantNotificationsHref = "/merchant-notifications";
  const hubNotificationsLabel = user?.isVirtualStore
    ? t("header.virtualShopNotifications")
    : t("header.merchantNotifications");
  // Role-aware labels for the account dropdown. Wording is intentionally
  // explicit so a virtual-store owner, a normal customer-seller, and a
  // pure buyer never see the same generic "طلباتي" string for distinct
  // flows (purchases vs. their store orders vs. orders on their personal
  // listings vs. incoming returns).
  const myPurchasesLabel = "مشترياتي";
  const myStoreOrdersLabel = "طلبات متجري";
  const myListingOrdersLabel = "طلبات إعلاناتي الخاصة";
  const incomingReturnsLabel = user?.isVirtualStore
    ? "طلبات الإرجاع الواردة من متجري"
    : isWholesaleOnlyRole
      ? "طلبات الإرجاع الواردة"
      : "طلبات الإرجاع الواردة على إعلاناتي";
  const { data: favorites } = useFavorites();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [location, navigate] = useLocation();
  const nativeShell = Capacitor.isNativePlatform();
  const goHomeInShell = (e: React.MouseEvent) => {
    if (!nativeShell) return;
    e.preventDefault();
    nativeTabNavigate("/");
  };
  
  // Fetch triggered alerts count
  const { data: triggeredAlerts } = useQuery<{ count: number }>({
    queryKey: ["/api/alerts/triggered-count"],
    enabled: !!user,
    refetchInterval: 60000, // Check every minute
  });

  // Fetch admin chat unread count (support messages from admin)
  const { data: adminChatUnread } = useQuery<{ unreadCount: number }>({
    queryKey: ["/api/admin-chat/unread"],
    enabled: !!user,
    refetchInterval: 30000,
  });
  const adminChatUnreadCount = adminChatUnread?.unreadCount || 0;

  const hasHeaderAttention =
    messagesUnreadCount > 0 ||
    merchantHubUnreadCount > 0 ||
    adminChatUnreadCount > 0 ||
    buyerOrderUnreadCount > 0 ||
    disputeUnreadCount > 0 ||
    notificationCount > 0;

  const favoritesCount = Array.isArray(favorites) ? favorites.length : 0;
  const alertsCount = triggeredAlerts?.count || 0;
  
  // All back surfaces (visible button, iOS edge-swipe, Android hardware
  // back) funnel through `appBack()` so they share a single deterministic
  // logical-back implementation. On web `appBack` falls back to
  // `window.history.back()` so browser back/forward arrows keep working.
  const handleGoBack = () => {
    appBack();
  };
  
  const isHomePage = location === "/";

  // Role-aware stores shortcut (same rule as the account menu's first item).
  const storesLinkHref = isStoreRole
    ? "/iraq-wholesale"
    : isWholesaleOnlyRole
      ? "/supplier-dashboard"
      : "/all-stores";
  const storesLinkLabel = isStoreRole
    ? t("header.wholesaleProducts")
    : isWholesaleOnlyRole
      ? t("header.wholesalePanel")
      : t("header.virtualStores");
  const showWorkWithUs = !user?.isVirtualStore && !isWholesaleOnlyRole;
  const accountDisplayName = !user
    ? ""
    : user.isVirtualStore
      ? (user.virtualStoreName || [user.firstName, user.lastName].filter(Boolean).join(" ") || t("header.user"))
      : ([user.firstName, user.lastName].filter(Boolean).join(" ") || t("header.user"));
  const todayHref = getTodayProductsHref(user);

  // Desktop web gets the slim top strip above the bar; phones, tablets and
  // the native shell (any width) get the quick-links strip below it. Both sit
  // OUTSIDE the sticky <header>, so the sticky height every page's offsets
  // assume (h-14 / sm:h-16) is unchanged and the strips scroll away. Only one
  // renders at a time, so exactly one QuickGuideModal trigger is mounted (the
  // guide opens only when asked; there is no first-visit auto-open).
  const isWideViewport = useMediaQuery("(min-width: 1024px)");
  const useDesktopTopStrip = isWideViewport && !nativeShell;

  // Desktop nav (lg+). "سوق الجملة" is skipped for store roles: their
  // role-aware stores link already points at /iraq-wholesale.
  const navLinks: { href: string; label: string; testId: string; active: boolean }[] = [
    {
      href: storesLinkHref,
      label: storesLinkLabel,
      testId: "link-all-stores",
      active: location.startsWith(storesLinkHref),
    },
    ...(isStoreRole
      ? []
      : [{
          href: "/iraq-wholesale",
          label: t("header.navWholesale"),
          testId: "link-nav-wholesale",
          active: ["/iraq-wholesale", "/china", "/turkey"].some((p) => location.startsWith(p)),
        }]),
    { href: "/friday", label: t("header.navFriday"), testId: "link-nav-friday", active: location.startsWith("/friday") },
    { href: todayHref, label: t("header.navToday"), testId: "link-nav-today", active: location.startsWith("/today") },
  ];

  const workWithUsPill = (testId: string, size: "sm" | "md") => (
    <Link
      href="/work-with-us-info"
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-950 font-bold whitespace-nowrap shadow-sm shadow-amber-500/30 transition-transform hover:scale-[1.03] ${
        size === "sm" ? "h-7 px-3 text-xs" : "h-8 px-2.5 text-[11px]"
      }`}
      data-testid={testId}
    >
      <span aria-hidden="true" className={size === "md" ? "hidden min-[400px]:inline" : undefined}>🚀</span>
      {t("header.workWithUs")}
    </Link>
  );

  return (
    <>
    {useDesktopTopStrip && (
      <div className="hidden lg:block bg-[#0a1c50] text-white pt-[env(safe-area-inset-top)]" data-testid="header-top-strip">
        <div className="container mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <p className="text-xs text-white/80 truncate" data-testid="text-header-tagline">{t("header.tagline")}</p>
          <div className="flex items-center gap-2 shrink-0">
            <QuickGuideModal variant="topbar" />
            {showWorkWithUs && workWithUsPill("link-work-with-us", "sm")}
          </div>
        </div>
      </div>
    )}
    <header className="sticky top-0 z-50 w-full">
      {/*
        `pt-[env(safe-area-inset-top)]` extends the green gradient up under the
        iOS status bar / dynamic island so the action row (back, home, avatar,
        cart, etc.) is pushed BELOW the notch instead of being overlapped and
        unclickable on iPhone. On Android the WebView already adjusts insets in
        MainActivity and `env(safe-area-inset-top)` resolves to 0px on every
        non-notch surface (Android, desktop browsers, iPad without notch), so
        this is a strict no-op everywhere except iPhone.
      */}
      <div className="gradient-primary shadow-lg pt-[env(safe-area-inset-top)]">
        <div className="container mx-auto px-1.5 sm:px-4 h-14 sm:h-16 flex items-center justify-between gap-1 sm:gap-4">
          <div className="flex items-center gap-1 md:gap-2 shrink-0 min-w-0">
            {/* Back and Home navigation buttons - show on all pages except home.
                Hidden on lg+, where the secondary bar below repeats both and
                the top row needs the room for the nav links. */}
            {!isHomePage && (
              <div className="flex lg:hidden items-center gap-0.5 sm:gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleGoBack}
                  className="h-8 w-8 sm:h-9 sm:w-9 text-white hover:bg-white/20"
                  data-testid="button-go-back"
                  title={t("header.back")}
                >
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
                <Link href="/" onClick={goHomeInShell}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 sm:h-9 sm:w-9 text-white hover:bg-white/20"
                    data-testid="button-go-home"
                    title={t("header.home")}
                  >
                    <Home className="h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </Link>
              </div>
            )}
            
            <Link href="/" onClick={goHomeInShell} className="flex items-center gap-1 sm:gap-3 group" data-testid="link-home">
              <Logo size="text-xl md:text-2xl" />
            </Link>
          </div>

          {/* Center desktop-only shortcuts (hidden on phones to keep header clean) */}
          <div className="hidden sm:flex items-center justify-end lg:justify-start flex-1 min-w-0 gap-0.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
            {/* Special Market badges - only show when on respective page.
                Tablet only (sm–lg): on lg+ the nav links mark the current
                market, and the bar has no room left for both. */}
            {location.startsWith("/today") && (
              <div 
                className="hidden sm:flex lg:hidden items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-full shadow-lg border border-amber-300/60"
                data-testid="badge-today"
              >
                <span className="text-sm sm:text-lg">📦</span>
                <span className="hidden min-[380px]:inline text-xs sm:text-sm font-bold text-amber-900 whitespace-nowrap">{t("header.todayProducts")}</span>
              </div>
            )}
            
            {location.startsWith("/friday") && (
              <div 
                className="hidden sm:flex lg:hidden items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full shadow-lg border border-green-400/60"
                data-testid="badge-friday"
              >
                <span className="text-sm sm:text-lg">🕌</span>
                <span className="hidden min-[380px]:inline text-xs sm:text-sm font-bold text-white whitespace-nowrap">{t("header.fridayMarket")}</span>
              </div>
            )}
            
            {location.startsWith("/shorja") && (
              <div 
                className="hidden sm:flex lg:hidden items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 bg-white/90 rounded-full shadow-lg border border-white/60"
                data-testid="badge-shorja"
              >
                <IraqFlag className="w-4 h-3 sm:w-6 sm:h-5 rounded-sm" />
                <span className="hidden min-[380px]:inline text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">{t("header.shorjaMarket")}</span>
              </div>
            )}
            
            {location.startsWith("/iraq-wholesale") && (
              <div 
                className="hidden sm:flex lg:hidden items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 bg-white/90 rounded-full shadow-lg border border-green-300/60"
                data-testid="badge-iraq-wholesale"
              >
                <IraqFlag className="w-4 h-3 sm:w-6 sm:h-5 rounded-sm" />
                <span className="hidden min-[380px]:inline text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">{t("market.iraqWholesale")}</span>
              </div>
            )}
            
            {location.startsWith("/china") && (
              <div 
                className="hidden sm:flex lg:hidden items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 bg-white/90 rounded-full shadow-lg border border-red-300/60"
                data-testid="badge-china"
              >
                <ChinaFlag className="w-4 h-3 sm:w-6 sm:h-5 rounded-sm" />
                <span className="hidden min-[380px]:inline text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">{t("header.chinaMarket")}</span>
              </div>
            )}
            
            {location.startsWith("/turkey") && (
              <div 
                className="hidden sm:flex lg:hidden items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 bg-white/90 rounded-full shadow-lg border border-red-300/60"
                data-testid="badge-turkey"
              >
                <TurkeyFlag className="w-4 h-3 sm:w-6 sm:h-5 rounded-sm" />
                <span className="hidden min-[380px]:inline text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">{t("header.turkeyMarket")}</span>
              </div>
            )}

            {/* Desktop nav (lg+). On narrower screens the same destinations
                live in the quick-links strip below the bar and on Home. */}
            <nav className="hidden lg:flex items-center gap-0.5 order-first" aria-label={t("header.menu")}>
              {navLinks.map((item) => (
                <Link
                  key={item.testId}
                  href={item.href}
                  className={`inline-flex items-center h-9 px-2 xl:px-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    item.active ? "bg-white/15 text-white" : "text-white/85 hover:bg-white/10 hover:text-white"
                  }`}
                  aria-current={item.active ? "page" : undefined}
                  data-testid={item.testId}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

          </div>

          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            {/* Language Switcher */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="h-8 sm:h-9 px-2 sm:px-3 text-white hover:bg-white/20 gap-1.5 shrink-0"
                  aria-label={LANGUAGES.find(l => l.code === language)?.nativeName || language}
                  title={LANGUAGES.find(l => l.code === language)?.nativeName || language}
                  data-testid="button-language-switcher"
                >
                  <Globe className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span
                    className={`${isHomePage ? "hidden min-[420px]:inline" : "hidden md:inline"} lg:hidden xl:inline text-xs sm:text-sm font-medium`}
                    data-testid="text-current-language"
                  >
                    {LANGUAGES.find(l => l.code === language)?.nativeName || language}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 rounded-xl">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`rounded-lg cursor-pointer gap-3 ${language === lang.code ? "bg-primary/10 font-bold" : ""}`}
                    data-testid={`menu-language-${lang.code}`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span>{lang.nativeName}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cart is hidden for non-buying tiers (wholesaler + store)
                per the 3-tier account policy — those accounts can't
                place orders, so a cart icon would be misleading. */}
            {!isWholesaleOnlyRole && !isStoreRole && (
              <div className="text-white">
                <CartSheet />
              </div>
            )}

            {/* Phone "add your ad" (the desktop buttons are further down).
                Guests go to login first, exactly like the desktop button. The
                label is dropped where the bar is tight (inner pages, <380px);
                on inner pages under 380px the button is dropped too — the
                BottomNav "+" is always there. */}
            {(user || isInitialized) && (
              <Link
                href={user ? "/create-listing" : "/login"}
                className={`${isHomePage ? "inline-flex" : "hidden xs:inline-flex"} sm:hidden shrink-0 items-center gap-1 h-8 px-2 xs:px-2.5 rounded-lg gradient-cta text-white text-xs font-bold shadow-md shadow-orange-500/30`}
                aria-label={t("header.addYourListing")}
                data-testid="button-add-listing-mobile"
              >
                <Plus className="w-4 h-4" />
                <span className={isHomePage ? "hidden xs:inline" : "hidden"}>{t("header.addYourListing")}</span>
              </Link>
            )}

            {/* Phone menu for guests: login/register first, then the
                shortcuts the desktop bar shows. Signed-in users keep the
                avatar menu below. */}
            {!user && isInitialized && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="sm:hidden h-8 w-8 text-white hover:bg-white/20"
                    data-testid="button-mobile-menu"
                    title={t("header.menu")}
                    aria-label={t("header.menu")}
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl p-2">
                  <Link href="/login">
                    <DropdownMenuItem className="rounded-lg cursor-pointer font-bold" data-testid="button-login-mobile">
                      <LogIn className="w-4 h-4 me-3 text-primary" />
                      {t("header.login")}
                    </DropdownMenuItem>
                  </Link>
                  <Link href="/register">
                    <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-mobile-register">
                      <UserPlus className="w-4 h-4 me-3" />
                      {t("header.createAccount")}
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuSeparator />
                  {navLinks.map((item) => (
                    <Link key={item.testId} href={item.href}>
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid={`menu-mobile-${item.testId}`}>
                        {item.label}
                      </DropdownMenuItem>
                    </Link>
                  ))}
                  {showWorkWithUs && (
                    <Link href="/work-with-us-info">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-mobile-work-with-us">
                        <Rocket className="w-4 h-4 me-3 text-amber-500" />
                        {t("header.workWithUs")}
                      </DropdownMenuItem>
                    </Link>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!user && !isInitialized && (
              <div
                className="sm:hidden h-8 w-8 rounded-full bg-white/15 animate-pulse shrink-0"
                aria-hidden="true"
              />
            )}

            {user ? (
              <>
                <Link href="/favorites" className="hidden sm:block">
                  <Button 
                    variant="ghost" 
                    className="text-white/90 rounded-xl relative gap-1.5"
                    aria-label={favoritesCount > 0 ? `${t("header.favorites")} (${favoritesCount})` : t("header.favorites")}
                    title={t("header.favorites")}
                    data-testid="button-favorites"
                  >
                    <Heart className="w-4 h-4" />
                    {favoritesCount > 0 && (
                      <span className="absolute -top-1 -left-1 bg-pink-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-lg" data-testid="badge-favorites-count">
                        {favoritesCount}
                      </span>
                    )}
                  </Button>
                </Link>
                
                <Link href={messagesHref} className="hidden sm:block">
                  <Button 
                    variant="ghost" 
                    className="text-white/90 rounded-xl relative gap-1.5"
                    aria-label={messagesUnreadCount > 0 ? `${t("header.messages")} (${messagesUnreadCount})` : t("header.messages")}
                    title={t("header.messages")}
                    data-testid="button-messages"
                    onClick={handleMessagesClick}
                  >
                    <MessageCircle className="w-4 h-4" />
                    {messagesUnreadCount > 0 && (
                      <span className="absolute -top-1 -left-1 gradient-gold text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-lg" data-testid="badge-unread-count">
                        {messagesUnreadCount}
                      </span>
                    )}
                    {notificationCount > 0 && (
                      <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-transparent animate-ping" data-testid="badge-new-notification-ping" />
                    )}
                  </Button>
                </Link>
                <div className="hidden sm:block">
                  {/* Signed-in shortcuts are icon buttons (title/aria-label):
                      with the CTA, the avatar and — on lg+ — the nav links,
                      the bar has no room for text labels (store accounts
                      overflowed even at 768px). */}
                  <NotificationBell variant="desktop" labelClassName="hidden" />
                </div>
                {(isWholesaleOnlyRole || !!user?.isVirtualStore || merchantHubUnreadCount > 0) && (
                  <Link href={merchantNotificationsHref} className="hidden sm:block" onClick={clearMerchantHubUnread}>
                    <Button
                      variant="ghost"
                      className="text-white/90 rounded-xl relative gap-1.5"
                      aria-label={merchantHubUnreadCount > 0 ? `${hubNotificationsLabel} (${merchantHubUnreadCount})` : hubNotificationsLabel}
                      title={hubNotificationsLabel}
                      data-testid="button-merchant-notifications"
                    >
                      <Bell className="w-4 h-4 text-purple-300" />
                      {merchantHubUnreadCount > 0 && (
                        <span className="absolute -top-1 -left-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-lg" data-testid="badge-merchant-unread-count">
                          {merchantHubUnreadCount > 9 ? "9+" : merchantHubUnreadCount}
                        </span>
                      )}
                    </Button>
                  </Link>
                )}

                <Link href="/create-listing" className="hidden sm:block">
                  <Button 
                    className="font-bold rounded-xl shadow-lg shadow-orange-500/30 gradient-cta text-white hover:opacity-90 transition-opacity px-3 md:px-4"
                    aria-label={t("header.addYourListing")}
                    data-testid="button-create-listing"
                  >
                    <Plus className="w-4 h-4 md:me-2" />
                    <span className="hidden md:inline">{t("header.addYourListing")}</span>
                  </Button>
                </Link>

                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex shrink-0 items-center gap-2 text-white px-1 sm:px-2 rounded-xl relative"
                      aria-label={accountDisplayName}
                      title={accountDisplayName}
                      data-testid="button-user-menu"
                    >
                      <div className="relative">
                        <Avatar className="h-9 w-9 ring-2 ring-white/30">
                          <AvatarImage src={user.profileImageUrl || undefined} />
                          <AvatarFallback className="bg-white/20 text-white font-semibold">
                            {user.firstName?.[0] || user.email?.[0]?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        {hasHeaderAttention ? (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full shadow-lg animate-pulse" data-testid="badge-avatar-notification"></span>
                        ) : null}
                      </div>
                      <div className="hidden xl:flex flex-col items-start gap-0.5">
                        <span className="text-sm font-medium truncate max-w-[120px]">
                          {user.isVirtualStore 
                            ? (user.virtualStoreName || [user.firstName, user.lastName].filter(Boolean).join(" ") || t("header.user"))
                            : ([user.firstName, user.lastName].filter(Boolean).join(" ") || t("header.user"))}
                        </span>
                        <span className={`text-[10px] leading-none font-bold px-1.5 py-0.5 rounded-sm ${accountTypeBadgeClass}`}>
                          {accountTypeBadgeText}
                        </span>
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 max-h-[min(70dvh,28rem)] overflow-y-auto rounded-xl p-2">
                    <div className="flex items-center gap-2 px-2 py-2 mb-1">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.profileImageUrl || undefined} />
                        <AvatarFallback className="bg-muted font-semibold text-xs">
                          {user.firstName?.[0] || user.email?.[0]?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-semibold truncate" data-testid="text-dropdown-store-name">
                          {accountDisplayName}
                        </span>
                        {isStoreRole && (
                          <span className="text-[11px] text-muted-foreground truncate" data-testid="text-dropdown-owner-name">
                            {hasPhysicalStorefront(user) ? t("header.physicalStore") : t("header.virtualStore")}
                          </span>
                        )}
                        <Badge
                          variant="secondary"
                          className={`text-[10px] w-fit px-1.5 py-0 leading-relaxed ${
                            isWholesaleOnlyRole
                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                              : isStoreRole
                                ? hasPhysicalStorefront(user)
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                  : "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300"
                                : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                          }`}
                          data-testid="badge-account-type"
                        >
                          {accountTypeBadgeText}
                        </Badge>
                      </div>
                    </div>
                    <Link href={
                      isStoreRole
                        ? "/iraq-wholesale"
                        : isWholesaleOnlyRole
                          ? "/supplier-dashboard"
                          : "/all-stores"
                    }>
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-all-stores">
                        <Store className="w-4 h-4 me-2 text-emerald-500" />
                        {isStoreRole
                          ? t("header.wholesaleProducts")
                          : isWholesaleOnlyRole
                            ? t("header.wholesalePanel")
                            : t("header.virtualStores")}
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    {!user?.isVirtualStore && !isWholesaleOnlyRole && (
                      <Link href="/work-with-us-info">
                        <DropdownMenuItem className="rounded-lg cursor-pointer bg-gradient-to-l from-amber-50 to-transparent dark:from-amber-900/20" data-testid="menu-work-with-us">
                          <Rocket className="w-4 h-4 me-2 text-amber-500" /> {t("header.workWithUs")}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    {user?.accountType === "buyer" && !user?.isVirtualStore && (
                      <>
                        <Link href="/work-with-us-info">
                          <DropdownMenuItem className="rounded-lg cursor-pointer bg-gradient-to-l from-violet-50 to-transparent dark:from-violet-900/20" data-testid="menu-open-virtual-store">
                            <Rocket className="w-4 h-4 me-2 text-violet-500" /> {t("header.openVirtualStore")}
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {isWholesaleOnlyRole && (
                      <>
                        {/* "لوحة الجملة" already appears once above (the
                            shared store/panel item) — no second copy here. */}
                        <Link href="/merchant-notifications" onClick={clearMerchantHubUnread}>
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-merchant-notifications">
                            <Bell className="w-4 h-4 me-2 text-purple-500" /> {t("header.merchantNotifications")}
                            {merchantHubUnreadCount > 0 && (
                              <span className="ms-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">
                                {merchantHubUnreadCount}
                              </span>
                            )}
                          </DropdownMenuItem>
                        </Link>
                        <Link href="/merchant/dropshipping-orders">
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-virtual-store-orders">
                            <ClipboardList className="w-4 h-4 me-2 text-cyan-500" /> {t("header.virtualStoreOrders")}
                          </DropdownMenuItem>
                        </Link>
                        <Link href="/merchant/direct-wholesale-orders">
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-direct-wholesale-orders">
                            <ShoppingCart className="w-4 h-4 me-2 text-amber-600" /> الطلبات المباشرة
                          </DropdownMenuItem>
                        </Link>
                        <Link href="/merchant/partner-stores">
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-partner-stores">
                            <Users className="w-4 h-4 me-2 text-indigo-500" /> {t("header.partnerStores")}
                          </DropdownMenuItem>
                        </Link>
                      </>
                    )}
                    {!!user?.isVirtualStore && (
                      <Link href="/merchant-notifications" onClick={clearMerchantHubUnread}>
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-virtual-shop-notifications">
                          <Bell className="w-4 h-4 me-2 text-purple-500" /> {hubNotificationsLabel}
                          {merchantHubUnreadCount > 0 && (
                            <span className="ms-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">
                              {merchantHubUnreadCount}
                            </span>
                          )}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    {/* Virtual-store owners get a single unified product
                        entry ("منتجاتي") that lands on /my-reseller-inventory.
                        Wholesale-only users sell ONLY through wholesale /
                        dropshipping flows (/supplier-dashboard +
                        /merchant/*) and must not be funneled into the
                        personal-listing management page — hiding this entry
                        prevents a wholesale merchant from seeing wholesale
                        items mislabelled as "إعلانات بضاعتي الخاصة" inside
                        /my-listings. Regular non-store users still see the
                        classic ad-listings entry. */}
                    {!user?.isVirtualStore && !isWholesaleOnlyRole && (
                      <Link href="/my-listings">
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-my-listings">
                          <Package className="w-4 h-4 me-2 text-primary" /> {t("header.myListings")}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    {/* «طلبات إعلاناتي الخاصة» — orders received against the
                        user's own personal classified listings via
                        /seller-dashboard (regular `orders` + `order_items`).
                        Shown ONLY to ordinary buyer-sellers: store owners
                        (virtual or physical) publish through their store
                        catalog (own products + dropshipping), never personal
                        classified ads, so this section is always empty for
                        them and is hidden to avoid confusion. Wholesale
                        merchants keep their /supplier-dashboard flow. */}
                    {isBuyerRole && (
                      <Link href="/seller-dashboard" onClick={() => { if (myListingOrdersUnreadCount > 0) markAllMerchantRead.mutate(); }}>
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-listing-orders">
                          <Inbox className="w-4 h-4 me-2 text-emerald-500" /> {myListingOrdersLabel}
                          {myListingOrdersUnreadCount > 0 && (
                            <span className="ms-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">
                              {myListingOrdersUnreadCount}
                            </span>
                          )}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    {user?.isVirtualStore && (
                      <>
                        <Link href="/my-reseller-inventory">
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-my-products">
                            <Package className="w-4 h-4 me-2 text-green-600" /> {t("header.myProducts")}
                          </DropdownMenuItem>
                        </Link>
                        <Link href={`/store/${user.id}`}>
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-my-store">
                            <Store className="w-4 h-4 me-2 text-violet-500" /> {t("header.viewMyStore")}
                          </DropdownMenuItem>
                        </Link>
                        <Link href="/stores/physical">
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-physical-stores">
                            <Briefcase className="w-4 h-4 me-2 text-emerald-500" /> {t("header.physicalStores")}
                          </DropdownMenuItem>
                        </Link>
                        <Link href="/virtual-shop/orders" onClick={() => { if (shopOrderUnreadCount > 0) markAllShopRead.mutate(); }}>
                          <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-store-orders">
                            <ClipboardList className="w-4 h-4 me-2 text-amber-500" /> {myStoreOrdersLabel}
                            {shopOrderUnreadCount > 0 && (
                              <span className="ms-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">
                                {shopOrderUnreadCount}
                              </span>
                            )}
                          </DropdownMenuItem>
                        </Link>
                      </>
                    )}
                    <Link href="/favorites">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-favorites">
                        <Heart className="w-4 h-4 me-2 text-pink-500" /> {t("header.favorites")}
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/recently-viewed">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-recently-viewed">
                        <Clock className="w-4 h-4 me-2 text-blue-500" /> {t("header.recentlyViewed")}
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/my-alerts">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-alerts">
                        <Bell className="w-4 h-4 me-2 text-yellow-500" /> {t("header.myAlerts")}
                        {alertsCount > 0 && (
                          <span className="ms-auto bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            {alertsCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/compare">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-compare">
                        <GitCompare className="w-4 h-4 me-2 text-purple-500" /> {t("header.compare")}
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/map">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-map">
                        <Map className="w-4 h-4 me-2 text-green-500" /> {t("header.mapView")}
                      </DropdownMenuItem>
                    </Link>
                    {/* «مشترياتي» — buyer purchases (`/my-orders`).
                        Hidden for wholesaler accounts under the 3-tier
                        policy (they never buy, so the page is empty and
                        the menu entry is just noise). Stores keep it
                        because they DO buy from wholesalers and need a
                        place to track those orders. */}
                    {!isWholesaleOnlyRole && (
                      <Link href="/my-orders" onClick={() => { if (buyerOrderUnreadCount > 0) markAllBuyerRead.mutate(); }}>
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-my-purchases">
                          <ClipboardList className="w-4 h-4 me-2 text-blue-500" /> {myPurchasesLabel}
                          {buyerOrderUnreadCount > 0 && (
                            <span className="ms-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">
                              {buyerOrderUnreadCount}
                            </span>
                          )}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    {/* Buyer-side returns inbox («إرجاعاتي»). Same
                        rule as above — wholesalers don't buy, so the
                        returns page is meaningless for them. */}
                    {!isWholesaleOnlyRole && (
                      <Link href="/returns">
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-my-returns">
                          <Undo2 className="w-4 h-4 me-2 text-blue-500" /> إرجاعاتي
                        </DropdownMenuItem>
                      </Link>
                    )}
                    <Link href={messagesHref} onClick={handleMessagesClick}>
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-messages">
                        <MessageCircle className="w-4 h-4 me-2 text-green-500" /> {t("header.messages")}
                        {notificationCount > 0 ? (
                          <span className="ms-auto bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">
                            {notificationCount}
                          </span>
                        ) : messagesUnreadCount > 0 ? (
                          <span className="ms-auto bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full font-medium">
                            {messagesUnreadCount}
                          </span>
                        ) : null}
                      </DropdownMenuItem>
                    </Link>
                    <Link href="/admin-chat">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-admin-chat">
                        <Mail className="w-4 h-4 me-2 text-blue-500" />
                        <span>{t("header.support")}</span>
                        {adminChatUnreadCount > 0 && (
                          <span className="ms-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
                            {adminChatUnreadCount > 9 ? "9+" : adminChatUnreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                    </Link>
                    {/* Phase 1D — Incoming returns inbox. Surfaces for any
                        user who actually plays a seller / shop-owner role:
                        wholesalers, virtual store owners, store owners, and
                        regular customers whose listings have already attracted
                        merchant-side traffic (proxied via `merchantHubUnreadCount`).
                        Buyers who have never sold anything see nothing here,
                        same as today. */}
                    {(isWholesaleOnlyRole || isStoreRole || !!user?.isVirtualStore || merchantHubUnreadCount > 0) && (
                      <Link href="/returns/incoming">
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-incoming-returns">
                          <Inbox className="w-4 h-4 me-2 text-orange-500" /> {incomingReturnsLabel}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    <Link href="/my-disputes">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-my-disputes">
                        <Scale className="w-4 h-4 me-2 text-red-500" /> {t("header.myDisputes")}
                        {disputeUnreadCount > 0 && (
                          <span className="ms-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse" data-testid="badge-disputes-unread">
                            {disputeUnreadCount > 99 ? "99+" : disputeUnreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                    </Link>
                    {(isWholesaleOnlyRole || isStoreRole || user?.virtualStoreName) && (
                      <Link href="/wallet">
                        <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-wallet">
                          <Wallet className="w-4 h-4 me-2 text-green-500" /> {t("header.wallet")}
                        </DropdownMenuItem>
                      </Link>
                    )}
                    <Link href="/settings">
                      <DropdownMenuItem className="rounded-lg cursor-pointer" data-testid="menu-settings">
                        <Settings className="w-4 h-4 me-2 text-muted-foreground" /> {t("header.settings")}
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator className="my-2" />
                    <DropdownMenuItem 
                      onClick={() => setShowLogoutConfirm(true)} 
                      className="rounded-lg cursor-pointer text-destructive focus:text-destructive"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 me-2" /> {t("header.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : !isInitialized ? (
              <div className="hidden sm:flex items-center gap-2">
                <div className="h-9 w-28 rounded-xl bg-white/15 animate-pulse" />
                <div className="h-9 w-36 rounded-xl bg-white/15 animate-pulse" />
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link href="/login">
                  <Button 
                    variant="outline" 
                    className="rounded-xl font-bold border-white/30 text-white hover:bg-white/10"
                    data-testid="button-login"
                  >
                    <LogIn className="w-4 h-4 me-2" />
                    {t("header.login")}
                  </Button>
                </Link>
                <Link href="/login">
                  <Button 
                    className="font-bold rounded-xl shadow-lg shadow-orange-500/30 gradient-cta text-white hover:opacity-90 transition-opacity"
                    data-testid="button-sell"
                  >
                    <Plus className="w-4 h-4 me-2" />
                    {t("header.addYourListing")}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/*
        Mobile User Navigation Bar.
        Was `sm:hidden` (= visible on phones only). Now `hidden` everywhere
        because the mobile/tablet-portrait shell already gives the user:
          - the BottomNav (Home / Categories / Add / Messages / Account)
          - the Avatar dropdown in the top bar (full action menu, identical
            content to what this strip exposed: My Listings, Favorites,
            My Orders, My Disputes, Merchant Notifications, Settings, Logout).
        Keeping the strip on phones doubled the chrome height and made the
        page feel like a desktop site. Leaving the JSX in place (but hidden)
        keeps the change reversible without losing any markup.
      */}
      {user && (
        <div className="hidden border-b border-[#0d2461]/40" style={{background: "linear-gradient(135deg, #0a1c50 0%, #0d2461 100%)"}}>
          <div className="container mx-auto px-1 py-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
            <div className="flex min-w-max items-stretch justify-center gap-1 pe-1">
            <Link href="/my-listings">
              <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10">
                <Package className="w-5 h-5" />
                <span className="text-[10px]">{t("header.myListings")}</span>
              </Button>
            </Link>
            {user.isVirtualStore && (
              <Link href="/stores/physical">
                <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10" data-testid="button-mobile-physical-stores">
                  <Briefcase className="w-5 h-5 text-emerald-400" />
                  <span className="text-[10px]">{t("header.stores")}</span>
                </Button>
              </Link>
            )}
            <Link href="/favorites">
              <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10 relative">
                <Heart className="w-5 h-5 text-pink-400" />
                <span className="text-[10px]">{t("header.favorites")}</span>
                {favoritesCount > 0 && (
                  <span className="absolute top-0 right-1 bg-pink-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {favoritesCount}
                  </span>
                )}
              </Button>
            </Link>
            {!isWholesaleOnlyRole && (
              <Link href="/my-orders" onClick={() => { if (buyerOrderUnreadCount > 0) markAllBuyerRead.mutate(); }}>
                <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-2.5 text-white/90 hover:bg-white/10 relative" data-testid="button-mobile-my-purchases">
                  <ClipboardList className="w-5 h-5 text-sky-400" />
                  <span className="text-[10px] max-w-[3.5rem] leading-tight text-center">{myPurchasesLabel}</span>
                  {buyerOrderUnreadCount > 0 && (
                    <span className="absolute top-0 right-0.5 bg-red-500 text-white text-[9px] min-w-[1rem] h-4 px-0.5 rounded-full flex items-center justify-center font-bold">
                      {buyerOrderUnreadCount > 9 ? "9+" : buyerOrderUnreadCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}
            <Link href="/my-disputes">
              <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-2.5 text-white/90 hover:bg-white/10 relative" data-testid="button-mobile-my-disputes">
                <Scale className="w-5 h-5 text-rose-400" />
                <span className="text-[10px] max-w-[3.5rem] leading-tight text-center">{t("header.myDisputes")}</span>
                {disputeUnreadCount > 0 && (
                  <span className="absolute top-0 right-0.5 bg-red-500 text-white text-[9px] min-w-[1rem] h-4 px-0.5 rounded-full flex items-center justify-center font-bold">
                    {disputeUnreadCount > 99 ? "99+" : disputeUnreadCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link href={messagesHref}>
              <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10 relative" onClick={handleMessagesClick}>
                <MessageCircle className="w-5 h-5 text-green-400" />
                <span className="text-[10px]">
                  {t("header.messages")}
                </span>
                {messagesUnreadCount > 0 && (
                  <span className="absolute top-0 right-1 gradient-gold text-black text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-lg" data-testid="badge-mobile-messages-notification">
                    {messagesUnreadCount}
                  </span>
                )}
                {notificationCount > 0 && (
                  <span className="absolute top-0 left-1 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-transparent animate-ping" data-testid="badge-mobile-new-notification-ping" />
                )}
              </Button>
            </Link>
            <NotificationBell variant="mobile" />
            {(isWholesaleOnlyRole || !!user.isVirtualStore || merchantHubUnreadCount > 0) && (
              <Link href={merchantNotificationsHref} onClick={clearMerchantHubUnread}>
                <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10 relative" data-testid="button-mobile-merchant-notifications">
                  <Bell className="w-5 h-5 text-purple-400" />
                  <span className="text-[10px]">{hubNotificationsLabel}</span>
                  {merchantHubUnreadCount > 0 && (
                    <span className="absolute top-0 right-1 bg-red-500 text-white text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-bold" data-testid="badge-mobile-merchant-unread-count">
                      {merchantHubUnreadCount > 9 ? "9+" : merchantHubUnreadCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}
            <Link href="/settings">
              <Button variant="ghost" size="sm" className="flex-col gap-0.5 h-auto py-1.5 px-3 text-white/90 hover:bg-white/10">
                <Settings className="w-5 h-5" />
                <span className="text-[10px]">{t("header.settings")}</span>
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex-col gap-0.5 h-auto py-1.5 px-3 text-red-400 hover:bg-red-500/20"
              onClick={() => setShowLogoutConfirm(true)}
              data-testid="button-logout-mobile"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-[10px]">{t("header.logout")}</span>
            </Button>
            </div>
          </div>
        </div>
      )}

      {/* Secondary Navigation - Home & Back buttons */}
      {!isHomePage && (
        <div className="hidden sm:block bg-muted/80 border-b">
          <div className="container mx-auto px-4 py-1.5 flex items-center gap-2">
            <Link href="/" onClick={goHomeInShell}>
              <Button variant="ghost" size="sm" className="gap-2 text-xs" data-testid="button-go-home">
                <Home className="w-3.5 h-3.5" />
                {t("header.home")}
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-2 text-xs" 
              onClick={handleGoBack}
              data-testid="button-go-back"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              {t("header.back")}
            </Button>
          </div>
        </div>
      )}

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className={isRTL ? "text-right" : "text-left"}>{t("header.logoutConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription className={isRTL ? "text-right" : "text-left"}>
              {t("header.logoutConfirmDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isRTL ? "flex-row-reverse gap-2" : "gap-2"}>
            <AlertDialogAction 
              onClick={() => logout()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("header.logoutConfirmYes")}
            </AlertDialogAction>
            <AlertDialogCancel>{t("header.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
    {!useDesktopTopStrip && (
      <div
        className={`${nativeShell ? "" : "lg:hidden "}bg-[#0a1c50] border-b border-white/10`}
        data-testid="header-quick-links"
      >
        <div className="container mx-auto px-2 sm:px-4 py-2 flex items-center gap-1 min-[400px]:gap-1.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {showWorkWithUs && workWithUsPill("link-work-with-us", "md")}
          <QuickGuideModal variant="pill" />
          <Link
            href={storesLinkHref}
            className="inline-flex shrink-0 items-center gap-1.5 h-8 px-2.5 rounded-xl border border-white/30 bg-white/5 text-white text-[11px] font-bold whitespace-nowrap transition-colors hover:bg-white/15"
            data-testid="link-all-stores-strip"
          >
            <Store className="hidden min-[400px]:block w-4 h-4 text-emerald-300" />
            {storesLinkLabel}
          </Link>
        </div>
      </div>
    )}
    </>
  );
}
